import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { 
  ChartContainer,
  ChartTooltip
} from "@/components/ui/chart";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer
} from "recharts";
import { ArrowUp, ArrowDown, Clock, Users, TrendingUp, Star } from "lucide-react";

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d'];

interface AdvancedAnalyticsProps {
  growthMetrics: {
    revenueGrowth: number;
    patientGrowth: number;
    avgRevenuePerPatient: number;
  };
  retentionMetrics: {
    returningPatients: number;
    retentionRate: number;
    avgDaysBetweenVisits: number;
  };
  doctorKPIs: {
    doctorId: string;
    doctorName: string;
    avgRevenuePerPatient: number;
    patientSatisfaction: number;
    efficiency: number;
  }[];
  timeAnalytics: {
    peakHours: { hour: number; count: number }[];
    peakDays: { day: string; count: number }[];
    avgWaitTime: number;
  };
}

export function AdvancedAnalytics({
  growthMetrics,
  retentionMetrics,
  doctorKPIs,
  timeAnalytics
}: AdvancedAnalyticsProps) {
  // Check if there's data to display
  const hasData = doctorKPIs.length > 0 || timeAnalytics.peakDays.length > 0;
  
  if (!hasData) {
    return (
      <div className="text-center py-12">
        <div className="text-4xl mb-4">📊</div>
        <h3 className="text-lg font-semibold mb-2">No Advanced Analytics Data Available</h3>
        <p className="text-muted-foreground">
          Advanced analytics will appear here once you have sufficient patient and doctor data.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid gap-3 md:grid-cols-3">
        <Card className="transition-all duration-200 hover:shadow-lg hover:scale-[1.02] cursor-pointer">
          <CardHeader className="flex flex-row items-center justify-between pb-1">
            <CardTitle className="text-xs font-medium">Revenue Growth</CardTitle>
            <TrendingUp className="h-3 w-3 text-muted-foreground" />
          </CardHeader>
          <CardContent className="pt-0">
            <div className="text-xl font-bold flex items-center">
              {growthMetrics.revenueGrowth.toFixed(1)}%
              {growthMetrics.revenueGrowth >= 0 ? (
                <ArrowUp className="h-3 w-3 text-green-500 ml-1" />
              ) : (
                <ArrowDown className="h-3 w-3 text-red-500 ml-1" />
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              vs last month
            </p>
          </CardContent>
        </Card>

        <Card className="transition-all duration-200 hover:shadow-lg hover:scale-[1.02] cursor-pointer">
          <CardHeader className="flex flex-row items-center justify-between pb-1">
            <CardTitle className="text-xs font-medium">Patient Retention</CardTitle>
            <Users className="h-3 w-3 text-muted-foreground" />
          </CardHeader>
          <CardContent className="pt-0">
            <div className="text-xl font-bold">
              {retentionMetrics.retentionRate.toFixed(1)}%
            </div>
            <p className="text-xs text-muted-foreground">
              {retentionMetrics.returningPatients} returning
            </p>
          </CardContent>
        </Card>

        <Card className="transition-all duration-200 hover:shadow-lg hover:scale-[1.02] cursor-pointer">
          <CardHeader className="flex flex-row items-center justify-between pb-1">
            <CardTitle className="text-xs font-medium">Average Wait Time</CardTitle>
            <Clock className="h-3 w-3 text-muted-foreground" />
          </CardHeader>
          <CardContent className="pt-0">
            <div className="text-xl font-bold">
              {timeAnalytics.avgWaitTime.toFixed(1)} days
            </div>
            <p className="text-xs text-muted-foreground">
              Registration to completion
            </p>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Doctor Performance</CardTitle>
            <CardDescription>Key performance indicators by doctor</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={doctorKPIs}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="doctorName" />
                  <YAxis yAxisId="left" orientation="left" stroke="#8884d8" />
                  <YAxis yAxisId="right" orientation="right" stroke="#82ca9d" />
                  <Tooltip />
                  <Legend />
                  <Bar yAxisId="left" dataKey="patientSatisfaction" fill="#8884d8" name="Satisfaction %" />
                  <Bar yAxisId="right" dataKey="efficiency" fill="#82ca9d" name="Efficiency %" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Peak Hours Analysis</CardTitle>
            <CardDescription>Patient distribution throughout the day</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={timeAnalytics.peakHours}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="hour" 
                    tickFormatter={(hour) => `${hour}:00`}
                  />
                  <YAxis />
                  <Tooltip 
                    formatter={(value) => [`${value}`, 'Patients']}
                    labelFormatter={(hour) => `${hour}:00`}
                  />
                  <Legend />
                  <Line type="monotone" dataKey="count" stroke="#FF8042" name="Patient Count" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>


    </div>
  );
} 